import React from 'react';
import graphql from 'babel-plugin-relay/macro';
import { createFragmentContainer } from 'react-relay';
import PropTypes from 'prop-types';
import withStyles from '@mui/styles/withStyles';
import {Button, Link, ListItemText, ListItemIcon, Grid, Menu, MenuItem, TableContainer, Table,  TableBody, TableCell, TableHead, TableRow, TableSortLabel, Tooltip, Typography} from '@mui/material';
import CheckCircle from '@mui/icons-material/CheckCircle';
import PauseCircleFilled from '@mui/icons-material/PauseCircleFilled';
import CancelCircleOutline from '@mui/icons-material/Cancel';
import PauseCircleOutline from '@mui/icons-material/PauseCircleOutline';
import PlayCircleOutline from '@mui/icons-material/PlayCircleOutline';

import HourglassEmpty from '@mui/icons-material/HourglassEmpty';
import PublishedWithChanges from '@mui/icons-material/PublishedWithChanges';
import Cancel from '@mui/icons-material/Cancel';
import CallMade from '@mui/icons-material/CallMade';
import AppLogo from '../ui/AppLogo';
import AbEnvironment from '../auth/AbEnvironment';
import ConfirmationDialog from '../ui/ConfirmationDialog';

import { green, red, blue, amber } from '@mui/material/colors';
import { format } from 'date-fns';

import CustomerAppFetch from '../account/CustomerAppFetch';

const abEnvironment = new AbEnvironment();

const styleSheet = theme => ({
    paper: {
        width: '100%',
        overflow: 'auto',
    },
    tblHead: {
      backgroundColor: '#f1f1f1',
    },
    tblHeadRow: {
      height: 32,
    },
    tblHeadCell: {
      textAlign: 'left',
      verticalAlign: 'bottom',
      borderBottom: 0,
    },
    tblHeadCellB: {
      textAlign: 'right',
      verticalAlign: 'bottom',
    },
    tblHeadCellC: {
      textAlign: 'center',
      verticalAlign: 'bottom',
      borderBottom: 0,
    },
    tblHeadCellWidth100: {
      textAlign: 'center',
      verticalAlign: 'bottom',
      borderBottom: 0,
      width: 100
    },
    tblHeadRow2: {
      height: 24,
    },
    tblHeadCell2: {
      textAlign: 'center',
      verticalAlign: 'top',
      //borderBottom: 0,
    },
    tblHeadRow3: {
      height: 32,
    },
    markup: {
      color: '#666666'
    },
    appgrid: {
      minWidth: 136,
    },
    appnameitem: {
      marginLeft: 8,
    },
    inactive: {
      fontStyle: 'italic'
    }
});

class SubscriptionsList extends React.Component {

  constructor(props) {
    super(props);
    this.state = {
      customerAppId: '',
      customerAppName: '',
      activeMenu: null,
      activeSharedLoginMenu: null,
      sharedLoginSub: false,
      suspendedMenu: null,
      confirmCancel: false
    };


  }

  getCurrencyString(theValue) {
    if (isNaN(theValue))
      return '';

    let currencyString = Number(theValue)
        .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

    return currencyString;
  }

  getPositiveCurrencyString(theValue) {
    if (isNaN(theValue))
      return '';

    if (theValue > 0) {
        let currencyString = Number(theValue)
        .toLocaleString("en-US", {style: "currency", currency: "USD", minimumFractionDigits: 2});

        return currencyString;
    }
    return '';
  }

  goTo(url) {
    /*
    const sterm = this.props.sterm;
    if (sterm != null)
    {
        localStorage.setItem('appsSterms', sterm);
    }
    */
    this.props.history.push(`${url}`);
  }
 
  goToExternalApp(action, customerAppId, appId, appLink) {
    abEnvironment.goToExternalApp(action, customerAppId, appId, appLink);
  }

  toggleOrder = (ob) => {
    this.props.toggleOrder(ob);
  } 

  handleSuspendedMenuClose = () => {
    this.setState({suspendedMenu: null});
  }
  handleSuspendedMenuClick = (event, customerAppId, customerAppName) => {
    this.setState({suspendedMenu: event.currentTarget, customerAppId: customerAppId, customerAppName: customerAppName});
  }

  handleActiveMenuClose = () => {
    this.setState({activeMenu: null});
  }

  handleActiveMenuClick = (event, customerAppId, customerAppName) => {
    this.setState({activeMenu: event.currentTarget, customerAppId: customerAppId, customerAppName: customerAppName});
  }

  
  handleActiveSharedLoginMenuClose = () => {
    this.setState({activeSharedLoginMenu: null});
  }

  handleActiveSharedLoginMenuClick = (event, customerAppId, customerAppName) => {
    this.setState({activeSharedLoginMenu: event.currentTarget, customerAppId: customerAppId, customerAppName: customerAppName});
  }

  render() {
    /***
     * There are 3 different use cases for this component:
     *  1. Reseller portal, viewing customer subscriptions
     *     This was the original use case for the component.  Each row is editable (open up
     *     customer app maintenance screen), and can add new subscriptions.
     *     Just show the bindcard charge.
     *
     *  2. Reseller portal, viewing own subscriptions
     *     For own account, everything is bill to self, so don't show
     *     the markup.  (Markup should be 0 on all of these).  So just show charge.
     *
     *  3. Customer portal, viewing subscriptions.
     *     But don't show individual markup - just overall charge - which will be
     *     charge plus markup.  This will get more complex when subscriptions can be transferred.
     *
     ***/
    const classes = this.props.classes;
    const thenodes = this.props.sl.nodes;
    const statusOk = green[300];
    const statusPause = blue[300];
    const statusWaiting = amber[400];
    const statusCancel = red[300];
    //const nearExpireColor = amber[500];
    //const expireCardColor = red[300];

    // Get the summary data from the last entry
    let thisMonthCharge = 0;
    let lastMonthCharge = 0;
    let ytdCharge = 0;
    let thisMonthMarkup = 0;
    let lastMonthMarkup = 0;
    let ytdMarkup = 0;

    let lastIdx = thenodes.length-1;
    if (lastIdx >= 0)
    {
      let lastNode = thenodes[lastIdx];
      thisMonthCharge = lastNode.ttlTmBcSum
      lastMonthCharge = lastNode.ttlLmBcSum
      ytdCharge = lastNode.ttlYtdBcSum
      thisMonthMarkup = lastNode.ttlTmMkSum
      lastMonthMarkup = lastNode.ttlLmMkSum
      ytdMarkup = lastNode.ttlYtdMkSum
    }

    if (this.props.thePage === "myApps") {

      // ------------------------------------------------
      // -------- Account's own subscriptions  ----------
      // ------------------------------------------------

      // Default to customer - charge plus markup
      let thisMonthChargeSummary = this.getCurrencyString(parseFloat(thisMonthCharge) + parseFloat(thisMonthMarkup));
      let lastMonthChargeSummary = this.getCurrencyString(parseFloat(lastMonthCharge) + parseFloat(lastMonthMarkup));
      let ytdChargeSummary       = this.getCurrencyString(parseFloat(ytdCharge) + parseFloat(ytdMarkup));

      if (this.props.theRole === "reseller") {
        // Reseller - just SaaS charge  (they look at charge report to see AppBind and network fees)
        thisMonthChargeSummary = this.getCurrencyString(parseFloat(thisMonthCharge));
        lastMonthChargeSummary = this.getCurrencyString(parseFloat(lastMonthCharge));
        ytdChargeSummary       = this.getCurrencyString(parseFloat(ytdCharge));
      }

      return (
        <div className={classes.paper}>
          <TableContainer>
            <Table>
              <TableHead className={classes.tblHead}>
                <TableRow className={classes.tblHeadRow}>
                  <TableCell className={classes.tblHeadCell}>App</TableCell>
                  <TableCell className={classes.tblHeadCellB}>Last&nbsp;30&nbsp;days</TableCell>
                  <TableCell className={classes.tblHeadCellB}>Previous&nbsp;30&nbsp;days</TableCell>
                  <TableCell className={classes.tblHeadCellB}>Year&nbsp;to&nbsp;date</TableCell>
                  <TableCell className={classes.tblHeadCellC}>Status</TableCell>
                  <TableCell className={classes.tblHeadCellWidth100}/>
                </TableRow>
                <TableRow className={classes.tblHeadRow3}>
                  <TableCell></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2">{thisMonthChargeSummary}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2">{lastMonthChargeSummary}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2">{ytdChargeSummary}</Typography></TableCell>
                  <TableCell/>
                  <TableCell/>
                </TableRow>
              </TableHead>
              <TableBody>

              <Menu
                id="action-menu"
                anchorEl={this.state.activeMenu}
                open={Boolean(this.state.activeMenu)}
                onClose={this.handleActiveMenuClose}>
                <MenuItem onClick={() => {this.suspend()}}>
                  <ListItemIcon>
                    <PauseCircleOutline />
                  </ListItemIcon>
                  <ListItemText primary="Suspend"/>
                </MenuItem>
                <MenuItem onClick={() => {this.confirmCancel(false)}}>
                  <ListItemIcon>
                    <CancelCircleOutline htmlColor={statusCancel}/>
                  </ListItemIcon>
                  <ListItemText primary="Cancel"/>
                </MenuItem>
              </Menu>

              <Menu
                id="action-menu"
                anchorEl={this.state.activeSharedLoginMenu}
                open={Boolean(this.state.activeSharedLoginMenu)}
                onClose={this.handleActiveSharedLoginMenuClose}>
                <MenuItem onClick={() => {this.confirmCancel(true)}}>
                  <ListItemIcon>
                    <CancelCircleOutline htmlColor={statusCancel}/>
                  </ListItemIcon>
                  <ListItemText primary="Cancel"/>
                </MenuItem>
              </Menu>
              <Menu
                id="suspended-menu"
                anchorEl={this.state.suspendedMenu}
                open={Boolean(this.state.suspendedMenu)}
                onClose={this.handleSuspendedMenuClose}>
                <MenuItem onClick={() => {this.unsuspend()}}>
                  <ListItemIcon>
                    <PlayCircleOutline htmlColor={statusOk}/>
                  </ListItemIcon>
                  <ListItemText primary="Unsuspend"/>
                </MenuItem>
                <MenuItem onClick={() => {this.confirmCancel(false)}}>
                  <ListItemIcon>
                    <CancelCircleOutline htmlColor={statusCancel}/>
                  </ListItemIcon>
                  <ListItemText primary="Cancel"/>
                </MenuItem>
              </Menu>


                { thenodes.map(n => {
                  // Default to customer - charge plus markup
                  let tmBcSum = '';
                  let lmBcSum = '';
                  let ytdBcSum = '';

                  if (n.cappPaymentOrder !== 2) {
                    tmBcSum  = this.getCurrencyString(parseFloat(n.tmBcSum) + parseFloat(n.tmMkSum));
                    lmBcSum  = this.getCurrencyString(parseFloat(n.lmBcSum) + parseFloat(n.lmMkSum));
                    ytdBcSum = this.getCurrencyString(parseFloat(n.ytdBcSum) + parseFloat(n.ytdMkSum));
                  }

                  let credentialStatus = 1; //needs work
                  if (n.useEmail === null && n.pwd !== null) {
                    credentialStatus = 0;
                  }
                  if (n.useEmail !== '' && n.pwd !== null) {
                    credentialStatus = 0;
                  }

                  // make sure link url is prefixed correctly
                  let appUrlWithProtocol = '';
                  if (n.appUrl || n.appUrl !== '') {
                    appUrlWithProtocol = (n.appUrl.startsWith("http://") || n.appUrl.startsWith("https://")) ? n.appUrl :  "https://" + n.appUrl;
                  }

                  if ((this.props.theRole === "reseller") || (this.props.transferredCustomer)) {

                    // ---------------------------------------------------------------------------
                    // -------- Reseller's or transferred customer's own subscriptions  ----------
                    // ---------------------------------------------------------------------------

                    // Reseller navigate to subscription
                    let sublink = 'subscription?customerAppId=' + n.customerAppId;

                    // Reseller - just SaaS charge  (they look at charge report to see AppBind and network fees)
                    tmBcSum  = this.getCurrencyString(parseFloat(n.tmBcSum));
                    lmBcSum  = this.getCurrencyString(parseFloat(n.lmBcSum));
                    ytdBcSum = this.getCurrencyString(parseFloat(n.ytdBcSum));

                    if (n.customerAppStatus !== 2)
                    {
                      let customerAppId = n.customerAppId;
                      let customerAppName = n.appName;

                      // App status is active
                      return (
                        <TableRow key={n.customerAppId}>
                          <TableCell>
                            <Grid container alignItems="center" className={classes.appgrid}>
                              <Grid item>
                                <AppLogo logoPath={n.appLogo} appName={n.appName} imageHeight='24px'/>
                              </Grid>
                              <Grid item className={classes.appnameitem}>
                                <Link component="button" 
                                      variant="body2"
                                      onClick={this.goTo.bind(this, sublink)}>
                                  {n.appName}
                                </Link>
                              </Grid>
                            </Grid>
                          </TableCell>
                          <TableCell align="right"><Typography variant="body2">{tmBcSum}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2">{lmBcSum}</Typography></TableCell>
                          <TableCell align="right"><Typography variant="body2">{ytdBcSum}</Typography></TableCell>
                          <TableCell align="center">
                            { n.customerAppStatus === 0 && n.bcStatus !== -1 && n.bcStatus !== 4 &&
                              
                                 <Button color="primary" 
                                  aria-owns={this.state.activeMenu ? 'simple-menu': undefined}
                                  aria-haspopup='true'
                                  disabled={false}
                                  onClick={(e) => this.handleActiveMenuClick(e, customerAppId, customerAppName)} >

                                      <CheckCircle htmlColor={statusOk}/>
                                  
                                </Button>
                                
                            }
                            { n.customerAppStatus === 0 && n.bcStatus !== -1 && n.bcStatus === 4 &&
                              
                                 <Button color="primary" 
                                  aria-owns={this.state.activeSharedLoginMenu ? 'simple-menu': undefined}
                                  aria-haspopup='true'
                                  disabled={false}
                                  onClick={(e) => this.handleActiveSharedLoginMenuClick(e, customerAppId, customerAppName)} >

                                      <CheckCircle htmlColor={statusOk}/>
                                  
                                </Button>
                                
                            }
                            { n.customerAppStatus === 0 && n.bcStatus === -1 &&
                              <Tooltip title="Waiting for funding source to be put on file">
                                <HourglassEmpty htmlColor={statusWaiting}/>
                              </Tooltip>
                            }
                            { n.customerAppStatus === 1 &&
                              
                                <Button color="primary" 
                                  aria-owns={this.state.suspendedMenu ? 'simple-menu': undefined}
                                  aria-haspopup='true'
                                  disabled={false}
                                  onClick={(e) => this.handleSuspendedMenuClick(e, customerAppId, customerAppName)} >

                                      <PauseCircleFilled htmlColor={statusPause}/>
                                  
                                </Button>
                            }
                            { n.customerAppStatus === 2 &&
                              <Cancel htmlColor={statusCancel}/>
                            }
                          </TableCell>
                          <TableCell align="center">
                            <Button color="primary" variant="outlined" 
                                    disabled={appUrlWithProtocol === "" || credentialStatus === 1}
                                    onClick={() => { this.goToExternalApp('appLogin', n.customerAppId, null, appUrlWithProtocol) }}>
                              Login<CallMade/>
                            </Button> 
                          </TableCell>
                        </TableRow>
                      );
                    } else {
                      // Inactive app
                      return (
                        <TableRow key={n.customerAppId}>
                          <TableCell>
                            <Grid container alignItems="center" className={classes.appgrid}>
                              <Grid item>
                                <AppLogo logoPath={n.appLogo} appName={n.appName} imageHeight='24px'/>
                              </Grid>
                              <Grid item className={classes.appnameitem}>
                                <Typography variant="body2" className={classes.inactive}>                                  
                                    {n.appName}
                                </Typography>
                              </Grid>
                            </Grid>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" className={classes.inactive}>
                                {tmBcSum}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2"  className={classes.inactive} >
                                {lmBcSum}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography variant="body2" className={classes.inactive}>                              
                                {ytdBcSum}
                            </Typography>
                          </TableCell>
                          <TableCell align="center">
                            { n.customerAppStatus === 0  && n.bcStatus !== -1 &&
                              <CheckCircle htmlColor={statusOk}/>
                            }
                            { n.customerAppStatus === 0 && n.bcStatus === -1 &&
                              <Tooltip title="Waiting for funding source to be put on file">
                                <HourglassEmpty htmlColor={statusWaiting}/>
                              </Tooltip>
                            }
                            { n.customerAppStatus === 1 &&
                              <PauseCircleFilled htmlColor={statusPause}/>
                            }
                            { n.customerAppStatus === 2 &&
                              <Cancel htmlColor={statusCancel}/>
                            }
                          </TableCell>
                          <TableCell/>
                        </TableRow>
                      );
                    }
                  } else {

                    // --------------------------------------------------------
                    // -------- Unauth customer's own subscriptions  ----------
                    // --------------------------------------------------------

                    // Check n.customerListSubscription
                    let sublink = null;
                    if (n.customerViewSubscription) {
                      sublink = 'subscriptionview?customerAppId=' + n.customerAppId;
                    }
                    if (n.customerListSubscription === 1) {
                      if (n.customerAppStatus !== 2)
                      {
                        /* Active */
                        return (
                          <TableRow key={n.customerAppId}>
                            <TableCell>
                              <Grid container alignItems="center">
                                <Grid item>
                                  <AppLogo logoPath={n.appLogo} appName={n.appName} imageHeight='24px' />
                                </Grid>
                                { sublink == null &&
                                <Grid item className={classes.appnameitem}>
                                  <Typography variant="subtitle2">{n.appName}</Typography>
                                </Grid>
                                }
                                { sublink != null &&
                                <Grid item className={classes.appnameitem}>
                                  <Link component="button" 
                                        variant="body2"
                                        onClick={this.goTo.bind(this, sublink)}>
                                    {n.appName}
                                  </Link>
                                </Grid>
                                }
                              </Grid>
                            </TableCell>
                            <TableCell align="right">{tmBcSum}</TableCell>
                            <TableCell align="right">{lmBcSum}</TableCell>
                            <TableCell align="right">{ytdBcSum}</TableCell>
                            <TableCell align="center">
                              { n.customerAppStatus === 0  && n.bcStatus !== -1 &&
                                <CheckCircle htmlColor={statusOk}/>
                              }
                              { n.customerAppStatus === 0 && n.bcStatus === -1 &&
                                <Tooltip title="Waiting for funding source to be put on file">
                                  <HourglassEmpty htmlColor={statusWaiting}/>
                                </Tooltip>
                              }
                              { n.customerAppStatus === 1 &&
                                <PauseCircleFilled htmlColor={statusPause}/>
                              }
                              { n.customerAppStatus === 2 &&
                                <Cancel htmlColor={statusCancel}/>
                              }
                            </TableCell>
                            <TableCell align="center">
                              <Button color="primary" variant="outlined" 
                                      disabled={appUrlWithProtocol === "" || credentialStatus === 1}
                                      onClick={() => { this.goToExternalApp('appLogin', n.customerAppId, null, appUrlWithProtocol) }}>
                                Login<CallMade/>
                              </Button> 
                            </TableCell>
                          </TableRow>
                        );
                      } else {
                        /* Inactive */
                        return (
                          <TableRow key={n.customerAppId}>
                            <TableCell>
                              <Grid container alignItems="center">
                                <Grid item>
                                  <AppLogo logoPath={n.appLogo} appName={n.appName} imageHeight='24px'/>

                                </Grid>
                                <Grid item className={classes.appnameitem}>
                                  <Typography variant="subtitle2">{n.appName}</Typography>
                                </Grid>
                              </Grid>
                            </TableCell>
                            <TableCell align="right">{tmBcSum}</TableCell>
                            <TableCell align="right">{lmBcSum}</TableCell>
                            <TableCell align="right">{ytdBcSum}</TableCell>
                            <TableCell align="center">
                              { n.customerAppStatus === 0  && n.bcStatus !== -1 &&
                                <CheckCircle htmlColor={statusOk}/>
                              }
                              { n.customerAppStatus === 0 && n.bcStatus === -1 &&
                                <Tooltip title="Waiting for funding source to be put on file">
                                  <HourglassEmpty htmlColor={statusWaiting}/>
                                </Tooltip>
                              }
                              { n.customerAppStatus === 1 &&
                                <PauseCircleFilled htmlColor={statusPause}/>
                              }
                              { n.customerAppStatus === 2 &&
                                <Cancel htmlColor={statusCancel}/>
                              }
                            </TableCell>
                            <TableCell/>
                          </TableRow>
                        );
                      }
                    } else {
                      return(null);
                    }
                  }
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </div>
      );
    } else {

      // ---------------------------------------------------------------
      // -------- Resellers's view of customer subscriptions  ----------
      // ---------------------------------------------------------------

      //we need to see if the customer uses budgets and if not approved.  That fact, combined with a subscription is so entered, then we know
      //why something is pending.
      let customerNeedsToApprove = false;
      if (this.props.auth.isAuthenticatedReseller()) {
        if (this.props.account.accountType === 4) {
          if (this.props.account.useBudgets >= 1 && this.props.account.latestBudgetApproved === 0) {
            customerNeedsToApprove = true;
          }
        }

      }

      let thisMonthChargeSummary = this.getCurrencyString(thisMonthCharge);
      let lastMonthChargeSummary = this.getCurrencyString(lastMonthCharge);
      let ytdChargeSummary       = this.getCurrencyString(ytdCharge);

      let theDir = 'asc';
      if (this.props.orderDir === 0) {
        theDir = 'desc';
      }
 
      return (
        <div className={classes.paper}>
          <TableContainer>
            <Table>
              <TableHead className={classes.tblHead}>
                <TableRow className={classes.tblHeadRow}>
                  <TableCell className={classes.tblHeadCell}></TableCell>
                  <TableCell className={classes.tblHeadCellB}>Last&nbsp;30&nbsp;days</TableCell>
                  <TableCell className={classes.tblHeadCellB}>Previous&nbsp;30&nbsp;days</TableCell>
                  <TableCell className={classes.tblHeadCellB}>Year&nbsp;to&nbsp;date</TableCell>
                  <TableCell className={classes.tblHeadCell} colSpan={6}/>
                </TableRow>
                <TableRow className={classes.tblHeadRow3}>
                  <TableCell>
                    <TableSortLabel
                      active={this.props.orderBy === 0}
                      direction={theDir}
                      onClick={() => this.toggleOrder(0) }>
                      App
                    </TableSortLabel>
                  </TableCell>
                  <TableCell align="right"><Typography variant="subtitle2">{thisMonthChargeSummary}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2">{lastMonthChargeSummary}</Typography></TableCell>
                  <TableCell align="right"><Typography variant="subtitle2">{ytdChargeSummary}</Typography></TableCell>
                  <TableCell align="right">Model</TableCell>
                  {/*<TableCell align="right">Last 4</TableCell>
                  <TableCell align="right">Expires</TableCell>*/}
                  <TableCell align="right">
                  <TableSortLabel
                      active={this.props.orderBy === 1}
                      direction={theDir}
                      onClick={() => this.toggleOrder(1) }>
                      Created
                    </TableSortLabel>

                  </TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center"></TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                
              <Menu
                id="action-menu"
                anchorEl={this.state.activeMenu}
                open={Boolean(this.state.activeMenu)}
                onClose={this.handleActiveMenuClose}>
                <MenuItem onClick={() => {this.suspend()}}>
                  <ListItemIcon>
                    <PauseCircleOutline />
                  </ListItemIcon>
                  <ListItemText primary="Suspend"/>
                </MenuItem>
                <MenuItem onClick={() => {this.confirmCancel(false)}}>
                  <ListItemIcon>
                    <CancelCircleOutline htmlColor={statusCancel}/>
                  </ListItemIcon>
                  <ListItemText primary="Cancel"/>
                </MenuItem>
              </Menu>

              <Menu
                id="action-menu"
                anchorEl={this.state.activeSharedLoginMenu}
                open={Boolean(this.state.activeSharedLoginMenu)}
                onClose={this.handleActiveSharedLoginMenuClose}>
                <MenuItem onClick={() => {this.confirmCancel(true)}}>
                  <ListItemIcon>
                    <CancelCircleOutline htmlColor={statusCancel}/>
                  </ListItemIcon>
                  <ListItemText primary="Cancel"/>
                </MenuItem>
              </Menu>

              <Menu
                id="suspended-menu"
                anchorEl={this.state.suspendedMenu}
                open={Boolean(this.state.suspendedMenu)}
                onClose={this.handleSuspendedMenuClose}>
                <MenuItem onClick={() => {this.unsuspend()}}>
                  <ListItemIcon>
                    <PlayCircleOutline htmlColor={statusOk}/>
                  </ListItemIcon>
                  <ListItemText primary="Unsuspend"/>
                </MenuItem>
                <MenuItem onClick={() => {this.confirmCancel(false)}}>
                  <ListItemIcon>
                    <CancelCircleOutline htmlColor={statusCancel}/>
                  </ListItemIcon>
                  <ListItemText primary="Cancel"/>
                </MenuItem>
              </Menu>

                { thenodes.map(n => {
                  let tmBcSum = this.getCurrencyString(n.tmBcSum);
                  let lmBcSum = this.getCurrencyString(n.lmBcSum);
                  let ytdBcSum = this.getCurrencyString(n.ytdBcSum);

                  let customerAppId = n.customerAppId;
                  let customerAppName = n.appName;
                  // Derive the model
                  let theModel = 'Expense to me';
                  if (n.cappPaymentOrder === 1) {
                    let theMarkup = Number(n.cappMarkupPercentage);
                    if (theMarkup >= 0) {
                      theModel = parseFloat(theMarkup).toFixed(2) + '% markup';
                    } else {
                      theMarkup = theMarkup * -1;
                      theModel = parseFloat(theMarkup).toFixed(2) + '% discount';
                    } 
                  } else if (n.cappPaymentOrder === 0) {
                    let theMarkup = Number(n.cappMarkupPercentage);
                    if (theMarkup >= 0) {
                      theModel = parseFloat(theMarkup).toFixed(2) + '% markup';
                    } else {
                      theMarkup = theMarkup * -1;
                      theModel = parseFloat(theMarkup).toFixed(2) + '% discount';
                    } 
                  }

                  let showLoginButton = true;

                  if (n.bcStatus === 4) {
                    //this is an auth only subscription so blank out fields that do not apply
                    tmBcSum = '';
                    lmBcSum = '';
                    ytdBcSum = '';
                    
                    theModel = 'Shared login';
                    if (n.taskType != null) {
                      if (n.taskType === 0) {
                        // This is credential sharing.
                        theModel = 'Shared login';
                      } else if (n.taskType === 1) {
                        theModel = 'Invite team members';
                        showLoginButton = false;
                      }
                    }
                  }

                  let created = format(new Date(n.created), 'yyyy-MM-dd');

                  let sublink = 'subscription?customerAppId=' + n.customerAppId;

                  let termsPending = false;
                  if (n.rPaymentOrder !== null || n.rAppMarkupPercentage !== null || n.rPassThroughFees !== null) {
                    termsPending = true;
                  }

                  // make sure link url is prefixed correctly
                  let loginDisabled = false;
                  let appUrlWithProtocol = '';
                  if (n.appUrl && n.appUrl !== '') {
                    appUrlWithProtocol = (n.appUrl.startsWith("http://") || n.appUrl.startsWith("https://")) ? n.appUrl :  "https://" + n.appUrl;
                  }
                  if (appUrlWithProtocol == null || appUrlWithProtocol === '') {
                    loginDisabled = true;
                  }

                  // ----------------------------------------------------------------------
                  // There are many combinations of state.  We'll simplify this in 2024.
                  // ----------------------------------------------------------------------
                  let subscriptionState = 0; // Active paid
                  // All active status
                  if (n.customerAppStatus === 0) {
                    if (n.bcStatus !== -1 && n.bcStatus !== 4) {
                      if (termsPending === false) {
                        subscriptionState = 0; // Active paid
                      } else {
                        subscriptionState = 1; // Active paid terms change pending
                      }
                    } else if (n.bcStatus === -1) {
                      if (n.soEntered === 0) {
                        // waiting for funding source
                        subscriptionState = 2; // Paid waiting for funding source
                      } else if (n.soEntered === 1) {
                        if (customerNeedsToApprove && n.cappPaymentOrder !== 2) {
                          subscriptionState = 3; // Paid pending customer approval of budget
                        } else {
                          subscriptionState = 4; // Paid pending funding source or customer approve
                        }
                      }
                    } else if (n.bcStatus === 4) {
                      if (termsPending === false) {
                        // Active shared login.  What type?
                        if (n.taskType != null) {
                          if (n.taskType === 0) {
                            if (n.stepState === 0) {
                              subscriptionState = 5; // Active shared login credentials pending
                              loginDisabled = true;
                            } else if (n.stepState === 1) {
                              subscriptionState = 6; // Active shared login credentials complete
                            }
                          } else if (n.taskType === 1) {
                            if (n.stepState === 0) {
                              subscriptionState = 7; // Active shared login invite pending
                            } else if (n.stepState === 1) {
                              subscriptionState = 8; // Active shared login invite complete
                            }
                          } else {
                            subscriptionState = 9; // Active shared login other
                          }
                        } else {
                          subscriptionState = 9; // Active shared login other
                        }
                      } else {
                        loginDisabled = true;
                        if (n.taskType != null) {
                          if (n.taskType === 0) {
                            subscriptionState = 10; // Terms pending, active shared login credentials 
                          } else if (n.taskType === 1) {
                            subscriptionState = 11; // Terms pending, active shared login invite
                          } else {
                            subscriptionState = 12; // Terms pending, active shared login other
                          }
                        } else {
                          subscriptionState = 12; // Terms pending, active shared login other
                        }
                      }
                    }
                  } else if (n.customerAppStatus === 1) {
                    if (n.bcStatus !== 4) {
                      subscriptionState = 101; // Paid suspended
                    } else {
                      if (n.taskType != null) {
                        if (n.taskType === 0) {
                          subscriptionState = 102; // Shared login credentials suspended
                        } else if (n.taskType === 1) {
                          subscriptionState = 103; // Shared login invite suspended
                        } else {
                          subscriptionState = 104; // Shared login other suspended
                        }
                      } else {
                        subscriptionState = 104; // Shared login other suspended
                      }
                    }
                  } else {
                    subscriptionState = 200; // Cancelled
                  }

                  if (n.customerAppStatus !== 2)
                  {
                    return (
                      <TableRow key={n.customerAppId}>
                        <TableCell>
                          <Grid container justifyContent="flex-start" alignItems="center" className={classes.appgrid}>
                            <Grid item>
                              <AppLogo logoPath={n.appLogo} appName={n.appName} imageHeight='24px'/>
                            </Grid>
                            <Grid item className={classes.appnameitem}>
                              <Link component="button" 
                                    variant="body2"
                                    onClick={this.goTo.bind(this, sublink)}>
                                {n.appName}
                              </Link>
                              {n.appSku != null && n.appSku !== '' && n.appSku !== n.appName &&
                              <Typography variant="body2">{n.appSku}</Typography>
                              }
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell align="right"><Typography variant="body2">{tmBcSum}</Typography></TableCell>
                        <TableCell align="right"><Typography variant="body2">{lmBcSum}</Typography></TableCell>
                        <TableCell align="right"><Typography variant="body2">{ytdBcSum}</Typography></TableCell>

                        <TableCell align="right">
                          <Typography variant="body2">
                            {theModel}
                          </Typography>
                        </TableCell>
                        {/*n.ccLast4 &&
                        <TableCell align="right">
                          <Typography variant="body2">
                            {'*' + n.ccLast4}
                          </Typography>
                        </TableCell>
                            */}
                        {/*!n.ccLast4 &&
                        <TableCell align="right">
                          <Typography variant="body2">
                            {''}
                          </Typography>
                        </TableCell>
                          */}
                        {/*<TableCell align="right">
                          <Typography variant="body2">
                            {n.ccExpy}
                          </Typography>
                        </TableCell>*/}
                        <TableCell align="right">
                          <Typography variant="body2">
                            {created}
                          </Typography>
                        </TableCell>

                        {/* Status cell */}
                        <TableCell align="center">
                          {/* Paid active */}
                          { subscriptionState === 0 &&
                            <Button color="primary" 
                            aria-owns={this.state.activeMenu ? 'simple-menu': undefined}
                            aria-haspopup='true'
                            disabled={false}
                            onClick={(e) => this.handleActiveMenuClick(e, customerAppId, customerAppName)}
                          >
                            <CheckCircle htmlColor={statusOk}/>
                          </Button>}

                          {/* Shared active tasks complete */}
                          { (subscriptionState === 6 || subscriptionState === 8 || subscriptionState === 9) &&
                            <Button color="primary" 
                            aria-owns={this.state.activeSharedLoginMenu ? 'simple-menu': undefined}
                            aria-haspopup='true'
                            disabled={false}
                            onClick={(e) => this.handleActiveSharedLoginMenuClick(e, customerAppId, customerAppName)}
                          >
                            <CheckCircle htmlColor={statusOk}/>
                          </Button>}

                          { (subscriptionState === 1 || subscriptionState === 10 || subscriptionState === 11 || subscriptionState === 12) &&
                          <Tooltip title="Waiting for customer to approve subscription terms changes">
                            <PublishedWithChanges htmlColor={statusWaiting}/>
                          </Tooltip>}

                          { subscriptionState === 2 &&
                          <Tooltip title="Waiting for funding source to be put on file">
                            <HourglassEmpty htmlColor={statusWaiting}/>
                          </Tooltip>}

                          { subscriptionState === 3 &&
                          <Tooltip title="Waiting for customer to approve budgets">
                            <HourglassEmpty htmlColor={statusWaiting}/>
                          </Tooltip>}
                          { subscriptionState === 4 &&
                          <Tooltip title="Waiting for your funding source to be added or customer to approve">
                            <HourglassEmpty htmlColor={statusWaiting}/>
                          </Tooltip>}

                          { subscriptionState === 5 &&
                          <Tooltip title="Waiting for your customer to share their credentials">
                            <HourglassEmpty htmlColor={statusWaiting}/>
                          </Tooltip>}
                          { subscriptionState === 7 &&
                          <Tooltip title="Waiting for your customer to invite your team">
                            <HourglassEmpty htmlColor={statusWaiting}/>
                          </Tooltip>}

                          { subscriptionState === 101 &&
                          <Button color="primary" 
                            aria-owns={this.state.suspendedMenu ? 'simple-menu': undefined}
                            aria-haspopup='true'
                            disabled={false}
                            onClick={(e) => this.handleSuspendedMenuClick(e, customerAppId, customerAppName)}
                          >
                            <PauseCircleFilled htmlColor={statusPause}/>                            
                          </Button>}
                          { (subscriptionState === 102 || subscriptionState === 103 || subscriptionState === 104) &&
                          <PauseCircleFilled htmlColor={statusPause}/>}
                          { subscriptionState === 200 &&
                          <Cancel htmlColor={statusCancel}/>}
                        </TableCell>
                        <TableCell align="center">
                          {showLoginButton === true &&
                          <Button color="primary" variant="outlined" 
                                  disabled={loginDisabled === true}
                                  onClick={() => { this.goToExternalApp('appLogin', n.customerAppId, null, appUrlWithProtocol) }}>
                            Login<CallMade/>
                          </Button>}
                        </TableCell>
                      </TableRow>
                    );
                  } else {
                    return (
                      <TableRow key={n.customerAppId}>
                        <TableCell>
                          <Grid container alignItems="center" className={classes.appgrid}>
                            <Grid item>
                              <AppLogo logoPath={n.appLogo} appName={n.appName} imageHeight='24px'/>
                            </Grid>
                            <Grid item className={classes.appnameitem}>
                              <Link component="button" 
                                    variant="body2"
                                    className={classes.inactive}
                                    onClick={this.goTo.bind(this, sublink)}>
                                {n.appName}
                              </Link>
                              {n.appSku != null && n.appSku !== '' && n.appSku !== n.appName &&
                              <Typography variant="body2">{n.appSku}</Typography>
                              }
                            </Grid>
                          </Grid>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" className={classes.inactive}>                           
                              {tmBcSum}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" className={classes.inactive}>
                              {lmBcSum}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2"  className={classes.inactive}>
                              {ytdBcSum}
                           </Typography>
                        </TableCell>

                        <TableCell align="right">
                          <Typography variant="body2"  className={classes.inactive}>
                            {theModel}
                          </Typography>
                        </TableCell>
                        {/*<TableCell align="right">
                          <Typography variant="body2">
                            {''}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {''}
                          </Typography>
                            </TableCell>*/}
                        <TableCell align="right">
                          <Typography variant="body2">
                            {created}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          { n.customerAppStatus === 0  && n.bcStatus !== -1 &&
                            <CheckCircle htmlColor={statusOk}/>
                          }
                          { n.customerAppStatus === 0 && n.bcStatus === -1 &&
                            <Tooltip title="Waiting for funding source to be put on file">
                              <HourglassEmpty htmlColor={statusWaiting}/>
                            </Tooltip>
                          }
                          { n.customerAppStatus === 1 &&
                            <PauseCircleFilled htmlColor={statusPause}/>
                          }
                          { n.customerAppStatus === 2 &&
                            <Cancel htmlColor={statusCancel}/>
                          }
                        </TableCell>
                        <TableCell align="right">
                        </TableCell>
                      </TableRow>
                    );
                  }
                })}
              </TableBody>
            </Table>
          </TableContainer>
          <ConfirmationDialog
            open={this.state.confirmCancel}
            onClose={() => { this.setState({confirmCancel:false});}}
            onConfirm={() => {this.cancel()}}
            onCancel={() => { }}
            title={"Cancel " + this.state.customerAppName + " subscription?"}
            cancelBtn="No"
            okBtn="Yes, cancel subscription"
            irreversibleAction={true}
          >
            {this.state.sharedLoginSub === true ? ' This subscription will only be visible when clicking the show cancelled subscriptions. ' : 
            'Cancelling this subscription will cancel the virtual purchase card causing all future charges to be declined.  '}

            Are you sure you want to proceed?
          </ConfirmationDialog>

        </div>
      );
    }
  }


  async suspend() {

    const { logError } = this.props.auth;
    const { callPartnerApi } = this.props.auth;

    let aPjwt = abEnvironment.getPjwt();

    CustomerAppFetch.fetch(this.state.customerAppId).toPromise()
    .then(caf => {
 
      let payload = {
        operation: "updateSubscriptionStatus",
        params: {
          pJwt: aPjwt,
          provisioningAccountId: caf.customerAppTByCustomerAppId.provisioningAccountId,
          customerAccountId: caf.customerAppTByCustomerAppId.customerAccountId,
          customerAppId: this.state.customerAppId,
          status: 1
        }
      };
  
  
      callPartnerApi('appbind/generic',
                     'POST',
                     payload
      )
      .then(resp => {
        return resp.json();
      })
      .then(response => {
  
        window.location.reload();
        this.setState({activeMenu: null, suspendedMenu: null, customerAppId: '', customerAppName: ''});
      })
      .catch(error => {
        logError('Exception in trying to suspend: ' + error);
        this.setState({activeMenu: null, suspendedMenu: null, customerAppId: '', customerAppName: ''});
      }); 
  
    })
    .catch(err => {
      //nothing to do....
    })

    
  }

  async unsuspend() {

 
    const { logError } = this.props.auth;
    const { callPartnerApi } = this.props.auth;

    let aPjwt = abEnvironment.getPjwt();

    CustomerAppFetch.fetch(this.state.customerAppId).toPromise()
    .then(caf => {
 
      let payload = {
        operation: "updateSubscriptionStatus",
        params: {
          pJwt: aPjwt,
          provisioningAccountId: caf.customerAppTByCustomerAppId.provisioningAccountId,
          customerAccountId: caf.customerAppTByCustomerAppId.customerAccountId,
          customerAppId: this.state.customerAppId,
          status: 0
        }
      };
  
  
      callPartnerApi('appbind/generic',
                     'POST',
                     payload
      )
      .then(resp => {
        return resp.json();
      })
      .then(response => {
  
        window.location.reload();
        this.setState({activeMenu: null, suspendedMenu: null, customerAppId: '', customerAppName: ''});
      })
      .catch(error => {
        logError('Exception in trying to suspend: ' + error);
        this.setState({activeMenu: null, suspendedMenu: null, customerAppId: '', customerAppName: ''});
      }); 
  
    })
    .catch(err => {
      //nothing to do....
    })

  }
  
  confirmCancel = (sharedLogin) => {
    this.setState({confirmCancel: true, activeMenu: null, activeSharedLoginMenu: null, suspendedMenu: null, sharedLoginSub: sharedLogin});
  };

  async cancel() {
     this.setState({confirmCancel: false});
    

     
    const { logError } = this.props.auth;
    const { callPartnerApi } = this.props.auth;

    let aPjwt = abEnvironment.getPjwt();

    CustomerAppFetch.fetch(this.state.customerAppId).toPromise()
    .then(caf => {
 
      let payload = {
        operation: "updateSubscriptionStatus",
        params: {
          pJwt: aPjwt,
          provisioningAccountId: caf.customerAppTByCustomerAppId.provisioningAccountId,
          customerAccountId: caf.customerAppTByCustomerAppId.customerAccountId,
          customerAppId: this.state.customerAppId,
          status: 2
        }
      };
  
  
      callPartnerApi('appbind/generic',
                     'POST',
                     payload
      )
      .then(resp => {
        return resp.json();
      })
      .then(response => {
  
        window.location.reload();
        this.setState({activeMenu: null, activeSharedLoginMenu: null, suspendedMenu: null, customerAppId: '', customerAppName: '', confirmCancel: false});
      })
      .catch(error => {
        logError('Exception in trying to suspend: ' + error);
        this.setState({activeMenu: null, activeSharedLoginMenu: null, suspendedMenu: null, customerAppId: '', customerAppName: '', confirmCancel: false});
      }); 
  
    })
    .catch(err => {
      //nothing to do....
    })


  }

}

SubscriptionsList.propTypes = {
  classes: PropTypes.object.isRequired,
};

export default createFragmentContainer(withStyles(styleSheet)(SubscriptionsList), { sl: graphql`
  fragment SubscriptionsList_sl on CustomerSubscriptionChargesConnection {
    nodes {
      ttlTmBcSum
      ttlLmBcSum
      ttlYtdBcSum
      ttlTmMkSum
      ttlLmMkSum
      ttlYtdMkSum
      pAccountId
      pAccountName
      appId
      appName
      appSku
      appLogo
      appUrl
      appSkuPrice
      appMarkupPercentage
      appMarkupAbsolute
      soEntered
      initiatedBy
      customerAppId
      customerAppStatus
      customerListSubscription
      customerViewSubscription
      bcStatus
      cappPaymentOrder
      cappSkuPrice
      cappMarkupPercentage
      cappMarkupAbsolute
      tmBcSum
      lmBcSum
      ytdBcSum
      tmMkSum
      lmMkSum
      ytdMkSum
      created
      updated
      ccLast4
      ccExpy
      rPaymentOrder
      rAppMarkupPercentage
      rPassThroughFees
      flowTaskId
      taskType
      taskData
      flowStepId
      stepState
      flowId
    }
  }
`});
